<script setup>
import {defineAsyncComponent, defineExpose, ref, watch} from 'vue';
import 'vue-multiselect/dist/vue-multiselect.css';
import FormButton from './../../pge-components/pge-button.vue';
import CheckboxInput from './../../pge-components/pge-checkbox.vue';
import IconPencil from "../icons/IconPencil.vue";
import FormAssetLibrary from "./FormAssetLibrary.vue";

const props = defineProps({
  background: {
    type: String,
    default: '#efefef'
  },
  max: Number,
  upload: {
    type: Boolean,
    default: false
  },
  tooltip: {
    type: String,
    default: ''
  },
  uploadDirect: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  focused: Boolean,
  labelText: {
    type: String,
    default: 'Select a Hub Logo'
  },
  accept: {
    type: String,
    default: 'image/*'
  },
  imageType: {
    type: String,
    default: 'images'
  },
  customElement: {
    type: Boolean,
    default: false
  },
  preview: String,
  class: String,
  min: Number,
  icon: String,
  template: String,
  iconSize: String,
  name: String,
  help: String,
  type: String,
  label: String,
  info: String,
  maxlength: String,
  placeholder: String,
  options: Array,
  description: String,
  model: Object,
  trackBy: String,
  labelPadding: String,
  mode: String,
  fileSize: {
    type: String,
    default: '',
  },
  driver: {
    type: String,
    default: 'public'
  },
  defaultValue: {
    type: [String, Number],
    default: null
  },
  rightButton: {
    type: Object,
    default: null
  },
  onChange: {
    type: Function,
    default: null
  },
  aspectRatio: {
    type: String,
    default: 'wide'
  },
  halfMarked: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['change', 'input', 'delete-pdf', 'delete-image']);
const icon = props.icon ? defineAsyncComponent(() => import(`../icons/Icon${props.icon}.vue`)) : null;
const inputEl = ref(null)
const VueMultiselect = defineAsyncComponent(() => import('vue-multiselect'));
const isFocused = ref(false);
const isModalOpen = ref(false);

watch(() => props.focused, (newValue) => {
  isFocused.value = newValue;
  if (isFocused.value) {
    inputEl.value.focus();
  }
});

const handleChange = (e) => {
  let value = e.target.value;
  if (props.type === 'number') value = Number(value);
  if (props.type === 'checkbox') value = e.target.checked;
  if (props.type === 'file') {
    return;
  }

  if (props.type !== 'file') {
    const obj = {name: props.name, value};
    emit('change', obj);
  }

  return true;
};

function generatePassword() {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const passwordLength = 7;
  let password = '';

  for (let i = 0; i <= passwordLength; i++) {
    const randomNumber = crypto.getRandomValues(new Uint32Array(1))[0] % chars.length;
    password += chars.substring(randomNumber, randomNumber + 1);
  }

  emit('update:modelValue', password);
}

function handleOnInput(e) {
  emit('update:modelValue', e.target.value);
  handleChange(e);
}

function openAssetLibrary(e = null) {
  if (e && e.preventDefault) {
    e.preventDefault();
    isModalOpen.value = true;
  }
}

defineExpose({ openAssetLibrary });
</script>

<template>
  <div :class="{
    [`form-${type}`]: true,
    'form-icon': icon,
    'form-input': true
  }">
    <input :class="props.class" :required="props.required" :id="`${props.name}`" v-if="['search'].indexOf(props.type) !== -1" :type="props.type"
           :name="props.name"
           :placeholder="props.placeholder" :value="props.model" @input="(e) => {handleOnInput(e)}"
           :style="{ background: props.background, borderColor: props.background }"/>

    <input :class="props.class" :required="props.required" :id="`${props.name}`" v-if="['switch'].indexOf(props.type) !== -1" type="checkbox"
           :name="props.name"
           @change="(e) => handleChange(e)" :checked="props.model" :disabled="props.disabled"
           @input="$emit('update:modelValue', $event.target.checked)"/>

    <checkbox-input
        :model-value="props.model"
        :name="props.name"
        :required="props.required"
        :onChange="(e) => handleChange(e)"
        :onInput="e => $emit('update:modelValue', e.target.checked)"
        :half-marked="props.halfMarked"
        v-if="['checkbox'].indexOf(props.type) !== -1"
    />

    <label
        v-if="mode !== 'compact'"
        :for="`${props.uploadDirect ? `field-upload-${props.name}` : props.name}`"
        :class="[
        labelPadding ? labelPadding : undefined,
        isFocused ? 'focused' : undefined,
        !model?.length && isFocused ? 'error' : undefined,
      ]"
    >
      <icon/>
      <template v-if="type === 'checkbox'">

        <div >
          {{ label }} <em v-if="required">*</em>
          <span>{{ info }}</span>
        </div>
      </template>
      <template v-else>
        <span v-if="props.label">{{ props.label }} <em v-if="props.required">*</em></span>
      </template>

      <span :class="`${props.customElement ? '' : 'preview-container'} assetLibrary`"
            v-if="['file'].indexOf(props.type) !== -1 && mode !== 'compact'">
        <span @click="(e) => openAssetLibrary(e)" class="pen-edit-asset-library"
              :style="props.uploadDirect ? { minWidth: '236px', minHeight: '144px', width:'100%', height:'100%'} : {}">
          <template v-if="props.preview">
            <template v-if="props.customElement">
              <div class="profile-picture-container">
                <div :class="`profile-picture ${props.fileSize}`">
                  <img :src="props.preview" alt="" v-if="props.preview" style="object-fit: cover"/>
                  <div class="upload-icon">
                    <div class="pencil-icon"></div>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <icon-pencil class="pen-edit-icon"/>
              <img :src="props.preview" alt="" v-if="props.preview" style="object-fit: cover; border-radius: 12px;"/>
            </template>
            <img src="" alt="" class="d-none">
          </template>
          <template v-else>
            <template v-if="props.customElement">
              <div class="profile-picture-container">
                <div :class="`profile-picture ${props.fileSize}`">
                  <svg v-if="props.fileSize === 'md'" width="112" height="112" viewBox="0 0 112 112" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M101.03 92.7517C94.3673 81.2323 84.0991 72.9723 72.116 69.0567C78.0434 65.528 82.6486 60.1512 85.2244 53.752C87.8003 47.3528 88.2043 40.2849 86.3744 33.6338C84.5445 26.9828 80.582 21.1162 75.0953 16.9352C69.6086 12.7541 62.9011 10.4897 56.0029 10.4897C49.1047 10.4897 42.3972 12.7541 36.9105 16.9352C31.4238 21.1162 27.4612 26.9828 25.6314 33.6338C23.8015 40.2849 24.2055 47.3528 26.7813 53.752C29.3572 60.1512 33.9624 65.528 39.8898 69.0567C27.9066 72.9679 17.6385 81.2279 10.9754 92.7517C10.731 93.1501 10.569 93.5934 10.4987 94.0555C10.4285 94.5176 10.4515 94.989 10.5664 95.4421C10.6814 95.8951 10.8859 96.3205 11.1679 96.6932C11.4499 97.066 11.8037 97.3784 12.2084 97.6122C12.6131 97.846 13.0605 97.9963 13.5243 98.0544C13.9881 98.1124 14.4588 98.077 14.9086 97.9502C15.3585 97.8234 15.7784 97.6077 16.1435 97.316C16.5087 97.0243 16.8117 96.6624 17.0348 96.2517C25.2773 82.0067 39.846 73.5017 56.0029 73.5017C72.1598 73.5017 86.7285 82.0067 94.971 96.2517C95.1941 96.6624 95.4971 97.0243 95.8623 97.316C96.2274 97.6077 96.6473 97.8234 97.0972 97.9502C97.547 98.077 98.0177 98.1124 98.4815 98.0544C98.9452 97.9963 99.3927 97.846 99.7974 97.6122C100.202 97.3784 100.556 97.066 100.838 96.6932C101.12 96.3205 101.324 95.8951 101.439 95.4421C101.554 94.989 101.577 94.5176 101.507 94.0555C101.437 93.5934 101.275 93.1501 101.03 92.7517ZM31.5029 42.0017C31.5029 37.156 32.9398 32.4192 35.6319 28.3902C38.324 24.3612 42.1504 21.2209 46.6271 19.3666C51.1039 17.5123 56.0301 17.0271 60.7826 17.9724C65.5351 18.9177 69.9006 21.2511 73.327 24.6775C76.7534 28.1039 79.0868 32.4694 80.0321 37.2219C80.9775 41.9745 80.4923 46.9006 78.6379 51.3774C76.7836 55.8542 73.6434 59.6806 69.6144 62.3727C65.5854 65.0648 60.8485 66.5017 56.0029 66.5017C49.5072 66.4947 43.2796 63.9112 38.6865 59.3181C34.0933 54.725 31.5098 48.4973 31.5029 42.0017Z" fill="#4B444D"/>
                  </svg>

                  <svg v-else width="54" height="51" viewBox="0 0 54 51" fill="none"
                       xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M52.7299 47C48.9224 40.4175 43.0549 35.6975 36.2074 33.46C39.5945 31.4436 42.2261 28.3712 43.698 24.7145C45.1699 21.0578 45.4007 17.019 44.3551 13.2184C43.3095 9.41778 41.0451 6.06548 37.9099 3.67631C34.7746 1.28714 30.9418 -0.00680542 26.9999 -0.00680542C23.0581 -0.00680542 19.2253 1.28714 16.09 3.67631C12.9547 6.06548 10.6904 9.41778 9.6448 13.2184C8.59916 17.019 8.83001 21.0578 10.3019 24.7145C11.7738 28.3712 14.4054 31.4436 17.7924 33.46C10.9449 35.695 5.07744 40.415 1.26994 47C1.13032 47.2277 1.0377 47.481 0.997566 47.745C0.95743 48.0091 0.970585 48.2785 1.03626 48.5374C1.10192 48.7962 1.21878 49.0393 1.37992 49.2523C1.54107 49.4653 1.74324 49.6439 1.97451 49.7775C2.20577 49.911 2.46146 49.9969 2.72646 50.0301C2.99147 50.0633 3.26044 50.043 3.5175 49.9706C3.77456 49.8981 4.0145 49.7749 4.22316 49.6082C4.43183 49.4415 4.605 49.2347 4.73244 49C9.44244 40.86 17.7674 36 26.9999 36C36.2324 36 44.5574 40.86 49.2674 49C49.3949 49.2347 49.5681 49.4415 49.7767 49.6082C49.9854 49.7749 50.2253 49.8981 50.4824 49.9706C50.7394 50.043 51.0084 50.0633 51.2734 50.0301C51.5384 49.9969 51.7941 49.911 52.0254 49.7775C52.2566 49.6439 52.4588 49.4653 52.62 49.2523C52.7811 49.0393 52.898 48.7962 52.9636 48.5374C53.0293 48.2785 53.0425 48.0091 53.0023 47.745C52.9622 47.481 52.8696 47.2277 52.7299 47ZM12.9999 18C12.9999 15.2311 13.821 12.5243 15.3594 10.222C16.8977 7.91973 19.0842 6.12531 21.6424 5.06568C24.2005 4.00605 27.0155 3.72881 29.7312 4.269C32.4469 4.80919 34.9415 6.14256 36.8994 8.1005C38.8574 10.0584 40.1907 12.553 40.7309 15.2687C41.2711 17.9845 40.9939 20.7994 39.9343 23.3576C38.8746 25.9157 37.0802 28.1022 34.7779 29.6406C32.4756 31.1789 29.7689 32 26.9999 32C23.2881 31.996 19.7295 30.5198 17.1048 27.8951C14.4802 25.2705 13.0039 21.7118 12.9999 18Z"
                        fill="#4B444D"/>
                  </svg>
                  <div class="upload-icon">
                    <div class="pencil-icon"></div>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <svg class="plus-icon" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M28 16C28 16.2652 27.8946 16.5196 27.7071 16.7071C27.5196 16.8946 27.2652 17 27 17H17V27C17 27.2652 16.8946 27.5196 16.7071 27.7071C16.5196 27.8946 16.2652 28 16 28C15.7348 28 15.4804 27.8946 15.2929 27.7071C15.1054 27.5196 15 27.2652 15 27V17H5C4.73478 17 4.48043 16.8946 4.29289 16.7071C4.10536 16.5196 4 16.2652 4 16C4 15.7348 4.10536 15.4804 4.29289 15.2929C4.48043 15.1054 4.73478 15 5 15H15V5C15 4.73478 15.1054 4.48043 15.2929 4.29289C15.4804 4.10536 15.7348 4 16 4C16.2652 4 16.5196 4.10536 16.7071 4.29289C16.8946 4.48043 17 4.73478 17 5V15H27C27.2652 15 27.5196 15.1054 27.7071 15.2929C27.8946 15.4804 28 15.7348 28 16Z" fill="#49454F"/>
              </svg>
              <!-- <svg class="plus-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M24 12C24 12.2652 23.8946 12.5196 23.7071 12.7071C23.5196 12.8946 23.2652 13 23 13H13V23C13 23.2652 12.8946 23.5196 12.7071 23.7071C12.5196 23.8946 12.2652 24 12 24C11.7348 24 11.4804 23.8946 11.2929 23.7071C11.1054 23.5196 11 23.2652 11 23V13H1C0.734784 13 0.48043 12.8946 0.292893 12.7071C0.105357 12.5196 0 12.2652 0 12C0 11.7348 0.105357 11.4804 0.292893 11.2929C0.48043 11.1054 0.734784 11 1 11H11V1C11 0.734784 11.1054 0.48043 11.2929 0.292893C11.4804 0.105357 11.7348 0 12 0C12.2652 0 12.5196 0.105357 12.7071 0.292893C12.8946 0.48043 13 0.734784 13 1V11H23C23.2652 11 23.5196 11.1054 23.7071 11.2929C23.8946 11.4804 24 11.7348 24 12Z"
                    fill="#49454F"/>
              </svg> -->
            </template>
          </template>
        </span>
      </span>
    </label>

    <span class="assetLibrary" v-if="type === 'file' && mode === 'compact'">
      <form-button
        :label="label"
        :template="props.template"
        :icon="props.icon"
        :tooltip="props.tooltip"
        button-size="small"
        @click="(e) => !props.uploadDirect ? openAssetLibrary(e) : null"
      />
    </span>
    <div class="form-input-password" v-if="['password'].indexOf(props.type) !== -1">
      <input :class="props.class" :required="props.required" :id="`${props.name}`" type="text" :name="props.name" :value="props.model"
             :maxlength="props.maxlength"
             @input="$emit('update:modelValue', $event.target.value)"
             @change="(e) => handleChange(e)"/>
      <form-button @click="generatePassword" label="Generate"/>
    </div>

    <div :class="{ 'form-group-flex': rightButton }">
      <input ref="inputEl" :class="[props.class, !model?.length && isFocused ? 'error' : undefined]" :required="props.required"
             :id="`${props.name}`" v-if="['text', 'number', 'email'].indexOf(props.type) !== -1" :type="props.type"
             :name="props.name" :placeholder="props.placeholder" :value="props.model"
             @input="(event) => {
          emit('input');
          emit('update:modelValue', event.target.value);
        }"
             @change="(e) => handleChange(e)"
             @focus="() => {
          isFocused = true;
          emit('update:focused', true);
        }"
             @blur="() => {
          isFocused = false;
          emit('update:focused', false);
        }"
             :style="{ background: props.background }" :maxlength="props.max"/>

      <input :class="props.class" :required="props.required" :id="`${props.name}`" v-if="['radio'].indexOf(props.type) !== -1" :type="props.type"
             :name="props.name" :placeholder="props.placeholder" :checked="props.model"
             @input="$emit('update:modelValue', $event.target.checked)" @change="(e) => handleChange(e)"
             :style="{ background: props.background }"/>

      <textarea ref="inputEl" :id="`${props.name}`" v-if="['textarea'].indexOf(props.type) !== -1" :name="props.name"
                :placeholder="props.placeholder" @change="(e) => handleChange(e)" v-text="props.defaultValue"
                :value="props.model"
                @input="(event) => {
          emit('input');
          emit('update:modelValue', event.target.value);
        }"
                :maxlength="props.max"
                :class="[props.class, !model?.length && isFocused ? 'error' : undefined]"
                @focus="() => {
          isFocused = true;
          emit('update:focused', true);
        }"
                @blur="() => {
          isFocused = false;
          emit('update:focused', false);
        }"></textarea>

      <select :id="`${props.name}`" v-if="['select'].indexOf(props.type) !== -1" :name="props.name" :value="props.model"
              @input="$emit('update:modelValue', $event.target.value)" @change="(e) => handleChange(e)">

        <option :key="`${props.name}-${option.value ? option.value : option}`"
                :value="option.value ? option.value : option" v-for="option in props.options">
          {{ option.text ? option.text : option }}
        </option>
      </select>

      <form-button v-if="rightButton && rightButton.action" @click="rightButton.action" size="sm" :label="rightButton.title"/>

      <a v-if="rightButton && rightButton.url" :href="rightButton.url">
        {{ rightButton.title }}
      </a>

      <div v-if="props.type === 'multiselect'">
        <!-- <vue-multiselect
            :max="4"
            :label="props.trackBy"
            :track-by="props.trackBy"
            deselect-label="Remove"
            select-label="Select"
            v-model="props.model"
            :options="props.options"
            :multiple="true"
            :close-on-select="true"
            placeholder="Select Speaker"
            @update:model-value="updateModelValue"
        >
          <template v-slot:noOptions>No members have been designated as speakers (see Members menu item).</template>
          <template v-slot:maxElements>The maximum number of speakers per session is four.</template>
        </vue-multiselect> -->
      </div>

      <form-asset-library
          v-if="isModalOpen"
          :value="props.value"
          :name="props.name"
          :preview="props.preview"
          :driver="props.driver"
          :labelText="props.labelText"
          :accept="props.accept"
          :imageType="props.imageType"
          :aspectRatio="props.aspectRatio"
          @change="(data) => $emit('change', data)"
          @delete-pdf="(data) => $emit('delete-pdf', data)"
          @delete-image="(data) => $emit('delete-image', data)"
          @close="isModalOpen = false"
      />
    </div>

    <div class="form-input-description" v-if="description && !isFocused">
      <p>{{ description }}</p>
    </div>
    <div class="form-input-description" v-else-if="description && isFocused">
      <p v-if="!model?.length" class="error">Cannot be blank</p>
      <p v-if="max" :class="!model?.length ? 'error' : undefined">
        {{ model?.length ?? 0 }} / {{ max }} Characters Maximum
      </p>
      <p v-else>{{ description }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-picture-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.profile-picture {
  width: 56px;
  height: 56px;
  border-radius: 100px;
  position: relative;

  img,
  svg {
    border-radius: 1000px;
    border: 1px solid var(--XL-M-Theme-sys-light-outline-variant, #CEC3CE);
    background: var(--XL-M-Theme-sys-light-surface-container-low, #FAF1F8);
  }

  svg {
    padding: 1px;
  }

  :is(img) {
    border-radius: 50%;
    height: 100%;
    width: 100%;
  }

  .upload-icon {
    position: absolute;
    bottom: 0;
    right: 0;
    display: flex;
    background: rgba(255, 255, 255, 1);
    width: 20px;
    height: 20px;
    gap: 8px;
    border-radius: 100px;
    border: 1px solid rgba(73, 69, 79, 1);

    svg {
      margin: auto;
    }
  }

  &.md {
    width: 112px;
    height: 112px;
    padding: 0;

    .upload-icon {
      transform: scale(1.6);
      right: 6px;
    }
  }
}

.pencil-icon {
  width: 10px;
  height: 10px;
  background-image: url('data:image/svg+xml,<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.65516 2.43921L7.56079 0.344364C7.49114 0.274702 7.40845 0.219442 7.31745 0.181741C7.22644 0.144039 7.1289 0.124634 7.03039 0.124634C6.93189 0.124634 6.83435 0.144039 6.74334 0.181741C6.65234 0.219442 6.56965 0.274702 6.5 0.344364L0.719847 6.12499C0.6499 6.19438 0.594444 6.27698 0.556703 6.368C0.518962 6.45901 0.499689 6.55662 0.500004 6.65514V8.74999C0.500004 8.9489 0.579021 9.13967 0.719674 9.28032C0.860326 9.42097 1.05109 9.49999 1.25 9.49999H3.34485C3.44338 9.5003 3.54098 9.48103 3.632 9.44329C3.72301 9.40555 3.80561 9.35009 3.875 9.28014L9.65516 3.49999C9.72482 3.43034 9.78008 3.34766 9.81778 3.25665C9.85549 3.16565 9.87489 3.0681 9.87489 2.9696C9.87489 2.87109 9.85549 2.77355 9.81778 2.68255C9.78008 2.59154 9.72482 2.50885 9.65516 2.43921ZM3.34485 8.74999H1.25V6.65514L5.375 2.53015L7.46985 4.62499L3.34485 8.74999ZM8 4.09436L5.90516 1.99999L7.03016 0.874989L9.125 2.96936L8 4.09436Z" fill="%2349454F" /></svg>');
  margin: auto;
}

.profile-picture-container:hover .upload-icon {
  background: #49454F;
  border: 1px solid #FAF1F8;
}

.profile-picture-container:hover .pencil-icon {
  background-image: url('data:image/svg+xml,<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.65516 2.43921L7.56079 0.344364C7.49114 0.274702 7.40845 0.219442 7.31745 0.181741C7.22644 0.144039 7.1289 0.124634 7.03039 0.124634C6.93189 0.124634 6.83435 0.144039 6.74334 0.181741C6.65234 0.219442 6.56965 0.274702 6.5 0.344364L0.719847 6.12499C0.6499 6.19438 0.594444 6.27698 0.556703 6.368C0.518962 6.45901 0.499689 6.55662 0.500004 6.65514V8.74999C0.500004 8.9489 0.579021 9.13967 0.719674 9.28032C0.860326 9.42097 1.05109 9.49999 1.25 9.49999H3.34485C3.44338 9.5003 3.54098 9.48103 3.632 9.44329C3.72301 9.40555 3.80561 9.35009 3.875 9.28014L9.65516 3.49999C9.72482 3.43034 9.78008 3.34766 9.81778 3.25665C9.85549 3.16565 9.87489 3.0681 9.87489 2.9696C9.87489 2.87109 9.85549 2.77355 9.81778 2.68255C9.78008 2.59154 9.72482 2.50885 9.65516 2.43921ZM3.34485 8.74999H1.25V6.65514L5.375 2.53015L7.46985 4.62499L3.34485 8.74999ZM8 4.09436L5.90516 1.99999L7.03016 0.874989L9.125 2.96936L8 4.09436Z" fill="%23FAF1F8"/></svg>');
}

/* demo */
body {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
}

.form-input {
  font-family: 'Lato', sans-serif;
  margin: 0 0 25px;
  position: relative;

  &.form-file {
    width: 100%;
  }

  * {
    box-sizing: border-box;
  }

  input,
  select,
  textarea {
    padding: 12px;
    border: 1px solid #79747E;
    background-color: #efefef;
    border-radius: 5px;
    font-weight: 500;
    font-size: 16px;
    line-height: 14px;
    width: 100%;
    display: block;
    margin: 0;
    outline: none;
    position: relative;
    z-index: 1;
  }

  select {
    height: 44.5px;
    padding: 0 12px !important;
  }

  input.title-1::placeholder, input.title-2::placeholder {
    font-style: italic;
  }

  input.title-1::placeholder {
    font-size: 15px;
  }

  input.title-2::placeholder {
    font-size: 12px;
  }

  input:not([type='checkbox']):not([type='radio']) {
    &:focus,
    &:active {
      box-shadow: none;
      border: 3px solid #583174 !important;
      padding: 10px !important;
    }
  }

  input:not([type='checkbox']):not([type='radio']).error {
    box-shadow: none;
    border: 3px solid #B3261E !important;
  }

  textarea.error {
    box-shadow: none;
    border: 3px solid #B3261E !important;
  }

  textarea {
    min-height: 100px;
    resize: vertical;

    &:focus,
    &:active {
      box-shadow: none;
      border: 3px solid #583174;
      padding: 10px !important;
    }
  }

  label {
    caret-color: transparent;
    font-weight: 400;
    font-size: 12px;
    line-height: 17px;
    // margin: 0 0 13px;
    display: flex;
    align-items: center;
    position: relative;
    z-index: 1;
  }

  label.focused {
    color: #583174;
  }

  label.error {
    color: #B3261E;
  }
}

.preview-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 15px;
  cursor: pointer;
  // width: fit-content;

  span {
    width: 150px;
    height: 150px;
    border: 1px dashed lightgray;
  }

  :is(img) {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .pen-edit-asset-library {
    display: flex !important;
    position: relative;
    justify-content: center;
    align-items: center;
    border-radius: 12px;
    width: 308px;
    height: 80px;

    &:hover {
      .plus-icon {
        background-color: rgba(73, 69, 79, 0.08);
      }

      .pen-edit-icon {
        color: #000;
        background: #FAF1F8;
        opacity: 1;
      }
    }

    &:active {
      .plus-icon {
        background: rgba(73, 69, 79, 0.12);
      }
    }

    .plus-icon {
      border-radius: 100%;
      padding: 6px;
      width: 38px;
      height: 38px;
    }

    .pen-edit-icon {
      width: 45px;
      height: auto;
      position: absolute;
      border-radius: 50%;
      padding: 5px;
      color: transparent;
      opacity: 0;
    }
  }
}

.form-file {
  label {
    display: block;

    span:first-child:not(.pen-edit-asset-library):not(.preview-container) {
      display: block;
      margin: 0 0 13px;
    }
  }

  input {
    position: absolute;
    opacity: 0;
    visibility: hidden;
    width: auto;
  }
}

.form-checkbox,
.form-radio {
  display: flex;
  align-items: center;

  input {
    width: 20px;
    height: 20px;
    margin-right: 13px;
    cursor: pointer;
  }

  label {
    order: 1;
    margin: 0;
    cursor: pointer;
  }
}

.form-checkbox {
  position: relative;

  label {
    width: 100%;
    font-size: 16px;

    div {
      display: flex;
      flex-direction: column;
      row-gap: 4px;
    }

    span {
      font-size: 12px !important;
      color: #49454F;
    }
  }

  .padding-left-sm {
    padding-left: 10px;
  }

  .padding-left-md {
    padding-left: 16px;
  }

  .padding-left-lg {
    padding-left: 22px;
  }

  input {
    opacity: 0;
    visibility: hidden;
    position: absolute;
    top: 0;
    left: 0;
  }
}

.form-switch {
  position: relative;

  input {
    position: absolute !important;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    bottom: 0;
  }

  label {
    position: relative;
    background-color: #808080;
    width: 42px;
    height: 22px;
    cursor: pointer;
    font-size: 0;
    color: transparent;
    border-radius: 22px;
    transition: background-color 500ms ease;
    margin: 0 auto;
  }
}

.form-search {
  position: relative;

  label {
    position: absolute;
    top: 7px;
    left: 10px;
  }

  input {
    padding-left: 40px;
  }
}

.form-input-password,
.form-group-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;

  input {
    border-radius: 5px 0 0 5px;
  }

  a {
    display: block;
    background-color: #583174;
    border-radius: 0 5px 5px 0;
    border: 0;
    padding: 13px 12px;
    color: #fff;
    cursor: pointer;
    font-weight: 400;
    font-size: 14px;
    line-height: 1;
    text-decoration: none;
    min-width: 100px;
    text-align: center;
  }

  .form-button {
    margin: 0;
  }
}

.d-block {
  display: block !important;
}

.d-none {
  display: none !important;
}

.form-input-description {
  font-size: 12px;
  font-weight: 300;
  line-height: 1.2;
  margin: 10px 0 0;
  color: gray;

  p {
    margin: 0;
  }

  .error {
    color: #B3261E;
    font-weight: 700;
  }
}

</style>
<style>
.multiselect__select:before {
  top: 80%;
}

.multiselect__tags {
  padding: 5px;
  padding-bottom: -2px !important;
  border: 1px solid #efefef;
  background-color: #efefef;
  outline: none;
  min-height: 49px;
}

.multiselect__tag {
  padding-top: 10px;
  padding-bottom: 10px;
  margin-bottom: 0px;
  border-radius: 20px;
}

.multiselect__tag, .multiselect__option--highlight, .multiselect__option--highlight::after {
  background-color: #583174 !important;
}

.multiselect__tag-icon {
  line-height: 32px;
  padding-right: 10px;
  font-weight: 100 !important;
}

.multiselect__tag-icon::after {
  color: white;
  font-size: 22px;
}

.multiselect__input[placeholder="Select Speaker"] {
  background-color: #efefef;
}

.multiselect__input[placeholder="Select Speaker"]:focus {
  margin-top: 5px;
}

.multiselect__placeholder {
  margin-top: 5px;
}
</style>
